import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { GlassButton } from '../ui';
import MediaLibraryModal from './MediaLibraryModal';
import { errText } from './apiErrorText';
import {
  SiteFavicon, getSiteFavicon, uploadSiteFavicon, setSiteFaviconFromLibrary, removeSiteFavicon,
} from '../../services/api';

interface Props {
  siteId: string;
  onStatus: (msg: string) => void;
}

type Busy = '' | 'upload' | 'library' | 'remove';

// Per-site settings for an imported site (currently: favicon). Every action persists immediately —
// there is no Apply/Save step and the editor iframe is NOT reloaded.
const ImportedSiteSettingsPanel: React.FC<Props> = ({ siteId, onStatus }) => {
  const [fav, setFav] = useState<SiteFavicon | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState<Busy>('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    setFav(null);
    setLoadFailed(false);
    getSiteFavicon(siteId)
      .then(f => { if (!cancelled) setFav(f); })
      .catch(() => {
        if (!cancelled) {
          setLoadFailed(true);
          onStatus('Could not load the favicon setting — reload to try again.');
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const isSet = !!fav?.favicon_url;
  // Fail closed: until the setting has loaded, no action is possible (no blind Remove).
  const disabled = busy !== '' || loadFailed || fav === null;

  const finish = (f: SiteFavicon) => {
    setFav(f);
    onStatus('Favicon updated — visitors see it on their next page load.');
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy('upload');
    try {
      finish(await uploadSiteFavicon(siteId, file));
    } catch (err: any) {
      onStatus('Favicon upload failed: ' + errText(err));
    } finally {
      setBusy('');
    }
  };

  const onLibrary = async (url: string) => {
    setShowLibrary(false);
    setBusy('library');
    try {
      finish(await setSiteFaviconFromLibrary(siteId, url));
    } catch (err: any) {
      onStatus('Favicon upload failed: ' + errText(err));
    } finally {
      setBusy('');
    }
  };

  const doRemove = async () => {
    setBusy('remove');
    try {
      setFav(await removeSiteFavicon(siteId));
      setConfirmRemove(false);
      onStatus('Favicon removed — the site\'s own icon (if any) is shown again.');
    } catch (err: any) {
      onStatus('Favicon removal failed: ' + errText(err));
    } finally {
      setBusy('');
    }
  };

  const tile: React.CSSProperties = {
    background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-sm)',
    padding: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <div style={{ padding: 16, borderTop: '1px solid var(--stroke)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Site settings</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Favicon</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.45 }}>
        Shown in browser tabs, bookmarks and iPhone home screens on every page of this site, including pages AiMe
        added. PNG, ICO or WebP, at least 32×32 px (180×180 or larger looks best), roughly square, up to 1 MB.
        Overrides any icon the uploaded site sets itself.
      </div>

      <div style={{ marginBottom: 10 }}>
        {isSet ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={tile}><img src={fav!.icon_32_url || ''} alt="Favicon preview" width={32} height={32} /></span>
            <span style={tile}><img src={fav!.apple_touch_url || ''} alt="Home screen icon preview" width={60} height={60} /></span>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {fav === null && !loadFailed
              ? 'Loading…'
              : 'No favicon set — the site\'s own icon (if it has one) is used.'}
          </div>
        )}
      </div>

      {fav && fav.warnings.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {fav.warnings.map((w, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, color: 'var(--text-secondary)' }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/webp,.ico"
        style={{ display: 'none' }}
        onChange={onPick}
        data-testid="favicon-file-input"
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <GlassButton variant="secondary" size="sm" disabled={disabled} onClick={() => fileRef.current?.click()}>
          {busy === 'upload' ? 'Uploading…' : isSet ? 'Replace favicon' : 'Upload favicon'}
        </GlassButton>
        <GlassButton variant="secondary" size="sm" disabled={disabled} onClick={() => setShowLibrary(true)}>
          {busy === 'library' ? 'Uploading…' : 'Choose from library'}
        </GlassButton>
        {isSet && !confirmRemove && (
          <GlassButton variant="secondary" size="sm" disabled={disabled} onClick={() => setConfirmRemove(true)}>
            Remove favicon
          </GlassButton>
        )}
        {isSet && confirmRemove && (
          <>
            <GlassButton variant="danger" size="sm" disabled={disabled} onClick={doRemove}>
              {busy === 'remove' ? 'Removing…' : 'Confirm remove'}
            </GlassButton>
            <GlassButton variant="secondary" size="sm" disabled={busy !== ''} onClick={() => setConfirmRemove(false)}>
              Cancel
            </GlassButton>
          </>
        )}
      </div>

      {showLibrary && (
        <MediaLibraryModal onSelect={onLibrary} onClose={() => setShowLibrary(false)} />
      )}
    </div>
  );
};

export default ImportedSiteSettingsPanel;
